// pages/Library.js
import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { getUser } from '../utils/auth';

const ADMIN_TIER_ROLES = ['admin', 'masteradmin', 'companyadmin', 'departmentadmin', 'trainingadmin'];

function Library() {
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const role = getUser()?.role;
  const isAdminTier = ADMIN_TIER_ROLES.includes(role);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/library/get_documents.php`, {
        withCredentials: true
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setDocuments(data);
    } catch (err) {
      setError('Failed to fetch documents: ' + (err.response?.data?.error || err.message));
      setDocuments([]);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', title.trim() || file.name);

    try {
      const uploadResponse = await axios.post(
        `${API_BASE_URL}/library/upload_document.php`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      if (uploadResponse.data.status !== 'success') {
        throw new Error(uploadResponse.data.message || 'Upload failed');
      }

      await fetchDocuments();
      setFile(null);
      setTitle('');
      e.target.reset();
      setSuccess('Document uploaded successfully!');
    } catch (err) {
      setError(err.message || 'Document upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    const result = await Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this document?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setError('');
    setSuccess('');

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/library/delete_document.php`,
        {
          data: { id: documentId },
          withCredentials: true
        }
      );

      if (response.data.status === 'success') {
        setDocuments(documents.filter(d => d.id !== documentId));
        setSuccess('Document deleted successfully!');
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete document');
    }
  };

  return (
    <div className="p-2">
      <h2>Library</h2>
      <p className="text-muted">Shared documents available to everyone.</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Uploaded By</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.length > 0 ? (
            documents.map((doc) => (
              <tr key={doc.id}>
                <td>{doc.name}</td>
                <td>{doc.uploaded_by}</td>
                <td>{new Date(doc.date).toLocaleDateString()}</td>
                <td>
                  <a
                    href={`${API_BASE_URL}/library/download_document.php?id=${doc.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-sm mr-1"
                  >
                    Download
                  </a>
                  {isAdminTier && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(doc.id)}
                    >
                      Delete
                    </Button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4">No documents found</td>
            </tr>
          )}
        </tbody>
      </Table>

      {isAdminTier && (
        <Form onSubmit={handleSubmit} className="mt-4 mb-4">
          <Form.Group className="mb-3">
            <Form.Label>Title (Optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter title for this document"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              If left blank, filename will be used
            </Form.Text>
          </Form.Group>
          <Form.Group>
            <Form.Label>Document</Form.Label>
            <Form.Control
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              required
              disabled={uploading}
            />
          </Form.Group>
          <Button variant="primary" type="submit" className="mt-2" disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Form>
      )}
    </div>
  );
}

export default Library;
